import { Alert, Box, Button, Card, Grid, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import * as yup from "yup";
import cnpjService from "services/cnpj.service";
import Service from "services/empresas";

export const TIPO_EMPRESA = { MATRIZ: "M", FILIAL: "F" };

const vazio = {
  razaoSocial: "",
  nomeFantasia: "",
  cnpj: "",
  inscricaoEstadual: "",
  tipo: TIPO_EMPRESA.MATRIZ,
  matriz: "",
  emissorNfe: "",
  cep: "",
  logradouro: "",
  numero: "",
  complemento: "",
  bairro: "",
  cidade: "",
  uf: "",
  telefone: "",
  email: "",
  ativo: true,
};

const schema = yup.object().shape({
  razaoSocial: yup.string().required("Esse campo é obrigatório"),
  cnpj: yup
    .string()
    .required("Esse campo é obrigatório")
    .test(
      "cnpj",
      "CNPJ deve ter 14 dígitos",
      (v) => (v || "").replace(/\D/g, "").length === 14
    ),
  matriz: yup.string().when("tipo", {
    is: TIPO_EMPRESA.FILIAL,
    then: (s) => s.required("Informe a matriz da filial"),
  }),
  uf: yup.string().max(2, "Use a sigla (ex.: SP)"),
  email: yup.string().email("E-mail inválido"),
});

const CAMPOS = [
  ["razaoSocial", "Razão social"],
  ["nomeFantasia", "Nome fantasia"],
  ["inscricaoEstadual", "Inscrição estadual"],
  ["emissorNfe", "Emissor de NF-e (ex.: prime-matriz)"],
  ["cep", "CEP"],
  ["logradouro", "Logradouro"],
  ["numero", "Número"],
  ["complemento", "Complemento"],
  ["bairro", "Bairro"],
  ["cidade", "Cidade"],
  ["uf", "UF"],
  ["telefone", "Telefone"],
  ["email", "E-mail"],
];

/** Cadastro e edição de empresa do grupo (sem id = nova). */
export default function FormEmpresa({
  currentIdAction = null,
  onClose,
  fetchData,
}) {
  const [empresas, setEmpresas] = useState([]);
  const [erro, setErro] = useState("");
  const [loading, setLoading] = useState(false);

  const fechar = () => {
    fetchData?.();
    onClose?.();
  };

  const formik = useFormik({
    initialValues: vazio,
    validationSchema: schema,
    onSubmit: async (values) => {
      setLoading(true);
      setErro("");
      try {
        const dados = { ...values, cnpj: values.cnpj.replace(/\D/g, "") };
        if (currentIdAction) await Service.update(currentIdAction, dados);
        else await Service.create(dados);
        fechar();
      } catch (e) {
        setErro(e?.error || e?.message || "Erro ao salvar a empresa.");
      }
      setLoading(false);
    },
  });

  useEffect(() => {
    (async () => {
      const { data } = await Service.list();
      setEmpresas((data || []).filter((e) => e._id !== currentIdAction));
      if (currentIdAction) {
        const empresa = await Service.get(currentIdAction);
        formik.setValues({
          ...vazio,
          ...Object.fromEntries(
            Object.entries(empresa).filter(
              ([, v]) => v !== null && v !== undefined
            )
          ),
          matriz: empresa.matriz?._id || empresa.matriz || "",
        });
      }
    })();

    const esc = (e) => e.key === "Escape" && fechar();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  const buscarCnpj = async () => {
    const cnpj = formik.values.cnpj.replace(/\D/g, "");
    if (cnpj.length !== 14) return;
    try {
      const r = await cnpjService.get(cnpj);
      const mapa = {
        razao_social: "razaoSocial",
        nome_fantasia: "nomeFantasia",
        cep: "cep",
        logradouro: "logradouro",
        numero: "numero",
        complemento: "complemento",
        bairro: "bairro",
        municipio: "cidade",
        uf: "uf",
        email: "email",
        ddd_telefone_1: "telefone",
      };
      Object.entries(mapa).forEach(([de, para]) => {
        if (r?.[de] && !formik.values[para]) formik.setFieldValue(para, r[de]);
      });
    } catch (e) {
      // consulta de CNPJ é só ajuda; sem resposta, preenche à mão
    }
  };

  const campo = (nome, rotulo, extra = {}) => (
    <TextField
      key={nome}
      id={nome}
      name={nome}
      label={rotulo}
      value={formik.values[nome]}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      error={formik.touched[nome] && Boolean(formik.errors[nome])}
      helperText={formik.touched[nome] && formik.errors[nome]}
      {...extra}
    />
  );

  return (
    <MDBox
      pt={"30px"}
      sx={{
        position: "absolute",
        zIndex: 9999,
        width: "100vw",
        minHeight: "100vh",
        top: 0,
        right: 0,
      }}
    >
      <MDBox
        sx={{
          position: "fixed",
          zIndex: -1,
          width: "100vw",
          height: "110%",
          backgroundColor: "rgba(0,0,0,0.5)",
          backdropFilter: "blur(5px)",
          top: 0,
          right: 0,
        }}
      />
      <Grid container spacing={6}>
        <Grid item xs={12} md={8} mx={"auto"}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              <MDTypography variant="h6" color="white" mt={1}>
                {currentIdAction ? "Editar empresa" : "Cadastrar empresa"}
              </MDTypography>
              <MDButton variant="contained" type="button" onClick={fechar}>
                Voltar
              </MDButton>
            </MDBox>
            <MDBox pt={3} mx={2} py={3} px={2}>
              <Box
                component="form"
                onSubmit={formik.handleSubmit}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 2,
                  mb: 2,
                }}
              >
                {campo("cnpj", "CNPJ", {
                  onBlur: (e) => (formik.handleBlur(e), buscarCnpj()),
                })}
                {campo("tipo", "Tipo", {
                  select: true,
                  SelectProps: { native: true },
                  children: [
                    <option key="M" value={TIPO_EMPRESA.MATRIZ}>
                      Matriz
                    </option>,
                    <option key="F" value={TIPO_EMPRESA.FILIAL}>
                      Filial
                    </option>,
                  ],
                })}
                {formik.values.tipo === TIPO_EMPRESA.FILIAL &&
                  campo("matriz", "Matriz", {
                    select: true,
                    SelectProps: { native: true },
                    InputLabelProps: { shrink: true },
                    sx: { gridColumn: "span 2" },
                    children: [
                      <option key="" value=""></option>,
                      ...empresas
                        .filter((e) => e.tipo !== TIPO_EMPRESA.FILIAL)
                        .map((e) => (
                          <option key={e._id} value={e._id}>
                            {e.nomeFantasia || e.razaoSocial}
                          </option>
                        )),
                    ],
                  })}
                {CAMPOS.map(([nome, rotulo]) => campo(nome, rotulo))}
                {campo("ativo", "Situação", {
                  select: true,
                  SelectProps: { native: true },
                  value: String(formik.values.ativo),
                  onChange: (e) =>
                    formik.setFieldValue("ativo", e.target.value === "true"),
                  children: [
                    <option key="1" value="true">
                      Ativa
                    </option>,
                    <option key="0" value="false">
                      Inativa
                    </option>,
                  ],
                })}

                {erro && (
                  <Alert severity="error" sx={{ gridColumn: "span 2" }}>
                    {erro}
                  </Alert>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    gridColumn: "span 2",
                    width: "100%",
                    color: "white !important",
                  }}
                >
                  {loading ? "Salvando..." : "Salvar"}
                </Button>
              </Box>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}
